/* eslint-disable import/prefer-default-export */
import ValidationStatus from '../../Data/ValidationStatus';
import { componentContainsCheckboxOptions } from '../../Helpers/components';
import { shouldShowComponent } from '../../Helpers/conditionality';
import { getErrorMessage } from '../../Helpers/validation';
import { getComponentValidationStatus } from '../__helpers__/component.helpers';
import { ComponentTypes } from '../../Data/Components';

export const getPageErrorMessage = (components) => {
  let pageErrorMessage;
  const componentsWithPageLevelValidation = Object.values(components).filter(
    (component) =>
      (component.component && component.component.type === 'radios') ||
      component.component.type === 'datepicker' ||
      componentContainsCheckboxOptions(component)
  );
  componentsWithPageLevelValidation.forEach((component) => {
    const errorMessage = getErrorMessage(component?.validation?.constraints);
    if (errorMessage) {
      pageErrorMessage = errorMessage;
    }
  });
  return pageErrorMessage;
};

export const getPageNumbersOfStaticLoaders = ({
  components,
  earliestSubmissionBlockPage,
}) => {
  const loaderComponents = Object.entries(components).filter(
    ([, value]) => value.component.type === 'loader'
  );

  if (loaderComponents) {
    return loaderComponents
      .map(([, value]) => value.component.page)
      .filter(
        (page) =>
          !earliestSubmissionBlockPage || page <= earliestSubmissionBlockPage
      );
  }
  return [];
};

export const currentPageContainsStaticLoader = (
  pageComponentIds,
  components
) => {
  const componentTypes = pageComponentIds.map((componentId) => {
    const { component } = components[`component_${componentId}`];
    return component.type;
  });

  return (
    componentTypes.length && componentTypes.some((type) => type === 'loader')
  );
};

export const getComponentIds = ({ components }) => {
  if (components) {
    return Object.values(components).map(({ component }) => component.id);
  }
  return [];
};

export const componentIdsIncludesTypes = ({
  components,
  componentIds,
  types,
}) => {
  const componentTypes = componentIds.map((componentId) => {
    const elementData = components[`component_${componentId}`];
    const { component } = elementData;
    const { type } = component;
    return type;
  });

  return componentTypes.some((type) => types.includes(type));
};

/**
 * If the component being interacted with is the last visible component on the page and the entry is valid then you will return true from this to trigger an advance to the next page
 */
export const shouldAutoAdvance = (pageComponent, pageComponents) => {
  const inputTypes = ['radios', 'textfield', 'select', 'checkboxes'];

  if (!inputTypes.includes(pageComponent.component.type)) {
    return false;
  }

  const otherIncompletePageComponents = Object.values(pageComponents).filter(
    (component) =>
      component.component.id !== pageComponent.component.id &&
      inputTypes.includes(component.component.type) &&
      getComponentValidationStatus({ component }) !== ValidationStatus.VALID
  );

  /**
   * If everything is complete return true
   */
  if (otherIncompletePageComponents.length === 0) {
    return true;
  }

  const visibleComponents = Object.values(pageComponents).filter(
    (component) => {
      if (component.input?.hiddenInChameleon) {
        return component.input.hiddenInChameleon === false;
      }
      return true;
    }
  );

  const visibleInReverse = visibleComponents.reverse();
  return (
    visibleInReverse.length > 0 &&
    visibleInReverse[0].component === pageComponent.component
  );
};

export const getVisiblePageComponents = (
  pageComponentIds,
  components,
  dynamicNewsletterName,
  phoneNumberCountryCode,
  subcategoryId
) => {
  if (!pageComponentIds || pageComponentIds.length === 0) {
    return [];
  }

  const visiblePageComponentIds = pageComponentIds.filter((componentId) => {
    const component = components[`component_${componentId}`];
    const isVisible = component?.component?.isVisible;
    const hiddenInChameleon = component?.input?.hiddenInChameleon;
    const type = component?.component?.type;

    if (hiddenInChameleon || type === 'hidden') {
      return false;
    }

    return (
      isVisible ||
      shouldShowComponent(
        components,
        component,
        dynamicNewsletterName,
        phoneNumberCountryCode,
        subcategoryId
      )
    );
  });

  return visiblePageComponentIds.map(
    (componentId) => components[`component_${componentId}`]
  );
};

export const isDynamicNewsletterOnForm = (components) => {
  const newsletterComponents = Object.entries(components).filter(
    ([, value]) => {
      return (
        value.component.type === ComponentTypes.NEWSLETTER_SIGNUP &&
        value?.component?.conditional?.onDynamicNewsletter !== undefined
      );
    }
  );
  return newsletterComponents.length > 0;
};
